import type { MediaUrl } from '@setplex/tria-api'
import type { BannerVisibleParams, TrailerFlowParams } from './index.h'

import { api } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { persist } from 'effector-storage/local'
import { delay, reset } from 'patronum'
import { register } from '~/core/repository'
import { model as router } from '~/core/router'
import { Intent as PlayerIntent, PlayerIntentMessage } from '~/entities/player'
import { model as session } from '~/entities/session'
import * as pinLockFeature from '~/features/pin-lock'
import { model as guestFeature } from '~/features/subscriber/guest'
import { model as config, remote } from '~/shared/config'
import { CONTENT_TYPE } from '~/shared/constants/content'
import { noop } from '~/shared/effector'
import { aye, isTvShowDetailsPathForShow } from '~/shared/helpers'
import { model as mq } from '~/shared/mq'
import {
  MIN_BANNER_AUTO_SCROLL_DURATION_MS,
  MIN_TRAILER_DURATION_SEC,
  NEXT_SLIDE_DELAY_MS,
  TRAILER_AUTOPLAY_DELAY_MS,
  TrailerContentType,
} from './index.h'

const shouldStopTrailerOnPathname = (
  {
    isActive,
    params,
  }: {
    isActive: boolean
    params: TrailerFlowParams | null
  },
  pathname: string | null
) =>
  isActive &&
  !(
    params?.contentType === TrailerContentType.TVSHOW &&
    isTvShowDetailsPathForShow(pathname, params.contentId)
  )

// *
// * EVENTS
// *

/** Initialize trailer flow - called when entering details page or after unlock */
export const initTrailerFlow = createEvent<TrailerFlowParams | void>()

/** Internal: start trailer flow (only if autoplay is allowed) */
const startTrailerFlow = createEvent<TrailerFlowParams>()

/** Stop trailer flow - called when leaving details page */
export const stopTrailerFlow = createEvent()

/** Called when trailer playback ended */
export const trailerEnded = createEvent()

/** Called when trailer playback error occurred */
export const trailerError = createEvent()

/** Set player visibility (from IntersectionObserver) */
export const setPlayerVisibility = createEvent<boolean>()

/** Called when trailer duration is determined from player's loadedmetadata event */
export const setTrailerDuration = createEvent<number>()

/** Set autoplay trailers setting for current user */
export const setIsAutoPlayTrailers = createEvent<{ value: boolean }>()

/** Internal: timer elapsed */
const timerElapsed = createEvent()

/** Internal: trailer is ready to play (url received + timer elapsed + duration valid) */
const trailerReadyToPlay = createEvent()

/** Internal: trailer duration is invalid (< 3 seconds) */
const trailerDurationInvalid = createEvent()

// Banner carousel events
/** Called when banner becomes visible in carousel */
export const onBannerBecameVisible = createEvent<BannerVisibleParams>()

/** Called when slide changes in carousel (stops trailer flow for the outgoing banner) */
export const onBannerSlideChanged = createEvent<BannerVisibleParams | void>()

// *
// * EFFECTS
// *

/** Load VOD trailer URL */
export const loadVodTrailerUrlFx = attach({
  effect: api.player.loadVodTrailerUrl,
})

/** Load TV Show trailer URL */
export const loadShowTrailerUrlFx = attach({
  effect: api.player.loadShowTrailerUrl,
})

let autoplayTimeoutId: ReturnType<typeof setTimeout> | undefined
let settleAutoplayTimer: ((elapsed: boolean) => void) | undefined

/** Drops the pending poster→trailer timeout, settling its effect as not elapsed. */
const clearAutoplayTimer = (): void => {
  if (autoplayTimeoutId === undefined) return
  clearTimeout(autoplayTimeoutId)
  autoplayTimeoutId = undefined
  const settle = settleAutoplayTimer
  settleAutoplayTimer = undefined
  settle?.(false)
}

/**
 * The poster→trailer delay, as an effect rather than `patronum/delay` so that
 * `cancelAutoplayTimerFx` can `clearTimeout` it. A timer left pending by a title
 * the user has navigated away from would otherwise start a trailer over the
 * content that replaced it.
 *
 * Resolves `true` when the delay elapsed, `false` when it was cancelled.
 */
const autoplayTimerFx = createEffect<void, boolean>(
  () =>
    new Promise<boolean>((resolve) => {
      clearAutoplayTimer()
      settleAutoplayTimer = resolve
      autoplayTimeoutId = setTimeout(() => {
        autoplayTimeoutId = undefined
        settleAutoplayTimer = undefined
        resolve(true)
      }, TRAILER_AUTOPLAY_DELAY_MS)
    })
)

const cancelAutoplayTimerFx = createEffect(clearAutoplayTimer)

// *
// * STORES
// *

/** Remote config: is trailer playback on background enabled */
export const $enabled = config.get(
  remote.tria_isTrailerPlaybackOnBackgroundEnabled
)

/** Autoplay trailers setting per account */
const $isAutoPlayTrailers = createStore<Record<string, boolean>>({})

/** Current user's autoplay trailers setting (treated as enabled for guest mode) */
export const $currentAutoPlayTrilers = combine(
  $isAutoPlayTrailers,
  session.$session,
  guestFeature.$guest,
  (isAutoPlayTrailers, session, guest) => {
    // In guest mode, consider autoplay setting enabled
    if (guest === true) return true
    const accountNumber = session?.accountNumber
    if (!accountNumber) return false
    return isAutoPlayTrailers[accountNumber] || false
  }
)

/** Trailer autoplay is allowed (both remote config and user setting are enabled) */
export const $isTrailerAutoplayAllowed = combine(
  $enabled,
  $currentAutoPlayTrilers,
  (enabled, autoPlay) => enabled && autoPlay
)

/** Current trailer flow params */
export const $trailerFlowParams = createStore<TrailerFlowParams | null>(
  null
).on(initTrailerFlow, (_, params) => (params ? params : null))

/*
 * Category name of auto trailer.
 */
export const $autoTrailerCategoryName = createStore<string>('').on(
  $trailerFlowParams,
  (_, params) => params?.categoryName ?? ''
)

/** Current trailer content ID */
export const $trailerContentId = $trailerFlowParams.map(
  (params) => params?.contentId ?? null
)

/** Current trailer content type for PlayerWrap */
export const $trailerContentType = $trailerFlowParams.map((params) =>
  params?.contentType === TrailerContentType.TVSHOW
    ? CONTENT_TYPE.SHOW
    : CONTENT_TYPE.VOD
)

/** Content name for player_open event_parameter4 (contentName) */
export const $trailerContentName = $trailerFlowParams.map(
  (params) => params?.contentName ?? null
)

/** Trailer content free status */
export const $trailerContentFree = $trailerFlowParams.map(
  (params) => params?.free ?? null
)

/** Trailer URL from API */
export const $trailerUrl = createStore<MediaUrl | null>(null).reset(
  initTrailerFlow
)

/** Is trailer URL loading */
export const $isTrailerUrlLoading = combine(
  loadVodTrailerUrlFx.pending,
  loadShowTrailerUrlFx.pending,
  (vodPending, showPending) => vodPending || showPending
)

/** Timer has elapsed (2.5 seconds passed) */
export const $timerElapsed = createStore(false).on(timerElapsed, () => true)

/** Trailer duration in seconds (from player's loadedmetadata event) */
export const $trailerDuration = createStore<number | null>(null).on(
  setTrailerDuration,
  (_, duration) => duration
)

/** Is trailer duration valid (>= 3 seconds) */
export const $isTrailerDurationValid = $trailerDuration.map(
  (duration) => duration !== null && duration >= MIN_TRAILER_DURATION_SEC
)

/** Has trailer URL loading error */
export const $hasTrailerUrlError = createStore(false)

/** Has trailer player playback error */
export const $hasTrailerPlayerError = createStore(false).on(
  trailerError,
  () => true
)

/** Has invalid trailer duration (< 3 seconds) */
export const $hasInvalidDuration = createStore(false).on(
  trailerDurationInvalid,
  () => true
)

/** Is trailer flow active */
export const $isTrailerFlowActive = createStore(false).on(
  initTrailerFlow,
  () => true
)

/** Should render trailer player (hasTrailer from params && no errors) */
export const $shouldRenderTrailerPlayer = combine(
  $isTrailerFlowActive,
  $trailerUrl,
  (isTrailerFlowActive, trailerUrl) => isTrailerFlowActive && aye(trailerUrl)
)

/** Is trailer player visible (duration is valid after loadedmetadata) */
export const $isTrailerPlayerVisible = createStore(false)
  .on(trailerReadyToPlay, () => true)
  .reset(trailerEnded, trailerError)

/** Is trailer currently playing (visible and not ended) */
export const $isTrailerPlaying = createStore(false)
  .on(trailerReadyToPlay, () => true)
  .reset(trailerEnded, trailerError, stopTrailerFlow)

// Banner carousel stores
/** Currently visible banner in carousel */
export const $visibleBanner = createStore<BannerVisibleParams | null>(null)
  .on(onBannerBecameVisible, (_, params) => params)
  .reset(stopTrailerFlow)

export const $visibleBannerId = $visibleBanner.map(
  (banner) => banner?.contentId ?? null
)

// Slot refs for trailer player portal
export const setSlotRef = createEvent<{
  contentId: number
  el: HTMLDivElement | null
  /** Node this ref callback had registered before (undefined on first attach). */
  prevEl?: HTMLDivElement | null
}>()

function isSlotInViewport(el: HTMLDivElement): boolean {
  if (typeof window === 'undefined') return true
  const rect = el.getBoundingClientRect()
  return rect.left >= 0 && rect.left < window.innerWidth
}

export const $slotsById = createStore<Record<string, HTMLDivElement | null>>(
  {}
).on(setSlotRef, (prev, { contentId, el, prevEl }) => {
  const key = String(contentId)
  const existing = prev[key]

  if (el === null) {
    // Only the slot's owner may release it. A detaching duplicate must not
    // evict the mounted slot that holds the trailer portal (FP-4222).
    if (prevEl != null && existing != null && existing !== prevEl) return prev
    if (!(key in prev)) return prev
    const next = { ...prev }
    delete next[key]
    return next
  }

  if (existing === el) return prev

  // Prefer slot in viewport: don't overwrite in-view slot with off-screen clone
  if (existing && !isSlotInViewport(el) && isSlotInViewport(existing)) {
    return prev
  }
  return { ...prev, [key]: el }
})

const slotRefCallbacks = new Map<
  number,
  (node: HTMLDivElement | null) => void
>()

/**
 * Returns a ref callback for skin-based banner trailer slots.
 *
 * The identity is cached per contentId on purpose: a fresh callback on every
 * render makes React detach (null) and re-attach the slot, which remounts the
 * trailer portal and tears down playback (FP-4222).
 */
export const getSlotRefCallback = (
  contentId: number
): ((node: HTMLDivElement | null) => void) => {
  const cached = slotRefCallbacks.get(contentId)
  if (cached) return cached

  let attached: HTMLDivElement | null = null
  const callback = (node: HTMLDivElement | null): void => {
    const prevEl = attached
    attached = node
    setSlotRef({ contentId, el: node, prevEl })
  }

  slotRefCallbacks.set(contentId, callback)

  return callback
}

/** Store holding getSlotRefCallback for skin pipe expressions (|entities/player/trailer/getSlotRefCallback) */
export const $getSlotRefCallback = createStore(getSlotRefCallback)

export const $playerSlotEl = combine(
  $slotsById,
  $visibleBannerId,
  (slots, visibleId) =>
    visibleId == null ? null : (slots[String(visibleId)] ?? null)
)

/** Clear url store on reset */
api.player.$currentPlayerPageUrl.reset(stopTrailerFlow)

/** Disable autoScroll when trailer flow is active and no errors occurred */
export const $autoScrollEnabledForTrailer = combine(
  {
    isTrailerFlowActive: $isTrailerFlowActive,
    hasTrailerUrlError: $hasTrailerUrlError,
    hasTrailerPlayerError: $hasTrailerPlayerError,
    hasInvalidDuration: $hasInvalidDuration,
    isTrailerAutoplayAllowed: $isTrailerAutoplayAllowed,
    trailerFlowParams: $trailerFlowParams,
  },
  ({
    isTrailerFlowActive,
    hasTrailerUrlError,
    hasTrailerPlayerError,
    hasInvalidDuration,
    isTrailerAutoplayAllowed,
    trailerFlowParams,
  }) => {
    if (!isTrailerAutoplayAllowed || !isTrailerFlowActive) return true

    // Enable autoScroll if flow is not active or if there are errors
    if (
      hasTrailerUrlError ||
      hasTrailerPlayerError ||
      hasInvalidDuration ||
      trailerFlowParams?.locked
    )
      return true

    // Disable autoScroll while trailer is loading/playing
    return false
  }
)

/** Trigger to programmatically go to next slide (incremented after trailer ends) */
export const $goToNextSlideTrigger = createStore(0)

// *
// * CONNECTIONS
// *

// Update autoplay trailers setting for current user
sample({
  clock: setIsAutoPlayTrailers,
  source: {
    session: session.$session,
    isAutoPlayTrilers: $isAutoPlayTrailers,
  },
  filter: ({ session }) => session != null,
  fn: ({ session, isAutoPlayTrilers }, { value }) => ({
    ...isAutoPlayTrilers,
    [session!.accountNumber]: value,
  }),
  target: $isAutoPlayTrailers,
})

// Persist autoplay trailers setting
// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $isAutoPlayTrailers,
  key: 'tria__is_auto_play_trailers',
  fail: noop,
})

// Start trailer flow only if autoplay is allowed
sample({
  clock: initTrailerFlow,
  source: $isTrailerAutoplayAllowed,
  filter: (isAllowed, params) => {
    return isAllowed && params != null && !params.locked
  },
  fn: (_, params) => params as TrailerFlowParams,
  target: startTrailerFlow,
})

// Start timer when trailer flow starts
// Timer triggers after 2.5 seconds
sample({
  clock: startTrailerFlow,
  target: autoplayTimerFx,
})

// Cancel the pending timer when the flow stops (page left, title switched)
sample({
  clock: stopTrailerFlow,
  target: cancelAutoplayTimerFx,
})

sample({
  clock: autoplayTimerFx.doneData,
  filter: (elapsed) => elapsed,
  target: timerElapsed,
})

// Load VOD trailer URL after timer elapsed (2.5 seconds)
sample({
  clock: timerElapsed,
  source: $trailerFlowParams,
  filter: (params): params is TrailerFlowParams =>
    Boolean(
      params &&
      params.contentType === TrailerContentType.VOD &&
      params.hasTrailer &&
      params.contentId
    ),
  fn: (params) => ({ vodId: params!.contentId }),
  target: loadVodTrailerUrlFx,
})

// Load TV Show trailer URL after timer elapsed (2.5 seconds)
sample({
  clock: timerElapsed,
  source: $trailerFlowParams,
  filter: (params): params is TrailerFlowParams =>
    Boolean(
      params &&
      params.contentType === TrailerContentType.TVSHOW &&
      params.hasTrailer &&
      params.contentId
    ),
  fn: (params) => ({ tvShowId: params!.contentId }),
  target: loadShowTrailerUrlFx,
})

// Handle VOD trailer URL response - verify contentId matches current flow
sample({
  clock: loadVodTrailerUrlFx.done,
  source: $trailerFlowParams,
  filter: (params, { params: reqParams }) =>
    Boolean(params) && params!.contentId === reqParams.vodId,
  fn: (_, { result }) => result,
  target: $trailerUrl,
})

// Handle TV Show trailer URL response - verify contentId matches current flow
sample({
  clock: loadShowTrailerUrlFx.done,
  source: $trailerFlowParams,
  filter: (params, { params: reqParams }) =>
    Boolean(params) && params!.contentId === reqParams.tvShowId,
  fn: (_, { result }) => result,
  target: $trailerUrl,
})

sample({
  clock: loadVodTrailerUrlFx.fail,
  source: $trailerFlowParams,
  filter: (params, { params: reqParams }) =>
    Boolean(params) && params!.contentId === reqParams.vodId,
  fn: () => true,
  target: $hasTrailerUrlError,
})

sample({
  clock: loadShowTrailerUrlFx.fail,
  source: $trailerFlowParams,
  filter: (params, { params: reqParams }) =>
    Boolean(params) && params!.contentId === reqParams.tvShowId,
  fn: () => true,
  target: $hasTrailerUrlError,
})

// Trailer is ready when: timer elapsed + duration is valid (from player's loadedmetadata)
sample({
  clock: setTrailerDuration,
  source: {
    isTrailerDurationValid: $isTrailerDurationValid,
    isFlowActive: $isTrailerFlowActive,
  },
  filter: ({ isTrailerDurationValid, isFlowActive }) =>
    isFlowActive && isTrailerDurationValid,
  target: trailerReadyToPlay,
})

// Trailer duration is invalid when: duration received but < 3 seconds
sample({
  clock: setTrailerDuration,
  source: {
    isFlowActive: $isTrailerFlowActive,
    hasInvalidDuration: $hasInvalidDuration,
  },
  filter: ({ isFlowActive, hasInvalidDuration }, duration) =>
    isFlowActive && !hasInvalidDuration && duration < MIN_TRAILER_DURATION_SEC,
  target: trailerDurationInvalid,
})

// Stop on slide change; restart is via onBannerBecameVisible only.
sample({
  clock: onBannerSlideChanged,
  target: stopTrailerFlow,
})

// Banner carousel: start trailer flow when banner becomes visible
// Conditions:
// - trailerPresent === true
// - not locked and not blockedByAcl (geo-blocked)
// - resourceType === 'INTERNAL'
// - autoScrollIn > 2 seconds
// - not already processing this contentId (prevent duplicate calls)
sample({
  clock: onBannerBecameVisible,
  source: $trailerFlowParams,
  filter: (currentParams, params) =>
    params.trailerPresent &&
    !params.locked &&
    !params.blockedByAcl &&
    params.resourceType === 'INTERNAL' &&
    params.autoScrollIn > MIN_BANNER_AUTO_SCROLL_DURATION_MS &&
    // Prevent duplicate initialization for same content
    currentParams?.contentId !== params.contentId,
  fn: (
    _,
    { contentType, contentId, locked, free, autoScrollEnabled, contentName }
  ) => ({
    contentType,
    contentId,
    hasTrailer: true,
    locked,
    free,
    autoScrollEnabled,
    categoryName: 'Banners',
    contentName,
  }),
  target: initTrailerFlow,
})

// Banner carousel: start trailer after successful PIN unlock,
// but only for currently visible banner.
sample({
  clock: pinLockFeature.unlockId,
  source: {
    visibleBanner: $visibleBanner,
    isAllowed: $isTrailerAutoplayAllowed,
  },
  filter: ({ visibleBanner, isAllowed }, unlockedId) =>
    isAllowed &&
    visibleBanner != null &&
    visibleBanner.contentId === unlockedId &&
    visibleBanner.trailerPresent &&
    !visibleBanner.blockedByAcl &&
    visibleBanner.resourceType === 'INTERNAL' &&
    visibleBanner.autoScrollIn > MIN_BANNER_AUTO_SCROLL_DURATION_MS,
  fn: ({ visibleBanner }) => ({
    contentType: visibleBanner!.contentType,
    contentId: visibleBanner!.contentId,
    hasTrailer: true,
    locked: false,
    free: visibleBanner!.free,
    autoScrollEnabled: visibleBanner!.autoScrollEnabled,
    categoryName: 'Banners',
    contentName: visibleBanner!.contentName,
  }),
  target: initTrailerFlow,
})

// Banner carousel: increment goToNextSlideTrigger 1 second after trailer ends
sample({
  clock: delay({
    source: trailerEnded,
    timeout: NEXT_SLIDE_DELAY_MS,
  }),
  source: {
    goToNextSlideTrigger: $goToNextSlideTrigger,
    trailerFlowParams: $trailerFlowParams,
  },
  filter: ({ trailerFlowParams }) => aye(trailerFlowParams?.autoScrollEnabled),
  fn: ({ goToNextSlideTrigger }) => goToNextSlideTrigger + 1,
  target: $goToNextSlideTrigger,
})

// Stop trailer flow when route changes, except same-show season switches
sample({
  clock: router.$pathname,
  source: {
    isActive: $isTrailerFlowActive,
    params: $trailerFlowParams,
  },
  filter: shouldStopTrailerOnPathname,
  fn: (_, pathname) =>
    new PlayerIntentMessage(
      undefined,
      pathname?.startsWith('/player')
        ? PlayerIntent.OtherContent
        : PlayerIntent.UserExit
    ),
  target: [mq.emit, stopTrailerFlow],
})

// Stop trailer flow when user session changes (account switch, sign out, etc.)
sample({
  clock: session.$session,
  source: $isTrailerFlowActive,
  filter: (isActive) => isActive,
  target: stopTrailerFlow,
})

// Reset all stores when flow stops
reset({
  clock: stopTrailerFlow,
  target: [
    $trailerFlowParams,
    $trailerUrl,
    $timerElapsed,
    $trailerDuration,
    $isTrailerPlayerVisible,
    $isTrailerFlowActive,
    $hasTrailerUrlError,
    $hasTrailerPlayerError,
    $hasInvalidDuration,
    $goToNextSlideTrigger,
    $slotsById,
  ],
})

// *
// * REGISTER FOR SKINS
// *

// Details page registers
register(
  $shouldRenderTrailerPlayer,
  '$routes/movies/details/shouldRenderTrailerPlayer'
)
register(
  $isTrailerPlayerVisible,
  '$routes/movies/details/isTrailerPlayerVisible'
)

// Banner carousel registers (shared between movies/root and shows/root)
register(onBannerBecameVisible, 'entities/player/trailer/onBannerBecameVisible')
register(onBannerSlideChanged, 'entities/player/trailer/onBannerSlideChanged')
register(
  $autoScrollEnabledForTrailer,
  '$entities/player/trailer/autoScrollEnabledForTrailer'
)
register($goToNextSlideTrigger, '$entities/player/trailer/goToNextSlideTrigger')
register($getSlotRefCallback, '|entities/player/trailer/getSlotRefCallback')
register($playerSlotEl, '$entities/player/trailer/playerSlotEl')
register(
  $isTrailerPlayerVisible,
  '$entities/player/trailer/isTrailerPlayerVisible'
)
register(
  $shouldRenderTrailerPlayer,
  '$entities/player/trailer/shouldRenderTrailerPlayer'
)
